import { useState, useCallback } from 'react';
import { JobApplicationFilters } from '../Interface';

export interface ActiveFilter {
  cardType: 'applicationStatus' | 'processStatus' | 'profileMatch' | 'aiInterviews' | 'onlineInterview' | 'uploadType';
  status: string;
}

interface FilterDefinition {
  key: keyof JobApplicationFilters;
  value: string | null;
  label: string;
  clearOthers?: (keyof JobApplicationFilters)[];
}

// Filter mapping configuration - declarative filter definitions
const FILTER_MAPPINGS: Record<string, FilterDefinition> = {
  // Application Status Card - Total (clears ALL filters)
  'applicationStatus:total': {
    key: 'status',
    value: null,
    label: 'All Applications',
    clearOthers: ['status', 'profileMatchSatus', 'roundStatus', 'interviewStatus', 'uploadType', 'requirementId', 'requirementMatch'],
  },
  'applicationStatus:totalApplied': {
    key: 'status',
    value: null,
    label: 'Total Applied',
    clearOthers: ['status'],
  },
  'applicationStatus:hired': {
    key: 'status',
    value: 'hired',
    label: 'Hired',
    clearOthers: ['status'],
  },
  'applicationStatus:rejected': {
    key: 'status',
    value: 'rejected',
    label: 'Rejected',
    clearOthers: ['status'],
  },
  'applicationStatus:shortlisted': {
    key: 'status',
    value: 'shortlisted',
    label: 'Shortlisted',
    clearOthers: ['status'],
  },

  // Process Status Card
  'processStatus:shortlisted': {
    key: 'status',
    value: 'shortlisted',
    label: 'Shortlisted',
    clearOthers: ['status'],
  },
  'processStatus:interviewScheduled': {
    key: 'status',
    value: 'interview',
    label: 'Interview Scheduled',
    clearOthers: ['status', 'interviewStatus'],
  },

  // Profile Match Card
  'profileMatch:qualified': {
    key: 'profileMatchSatus',
    value: 'qualified',
    label: 'Profile Match Qualified',
    clearOthers: ['profileMatchSatus'],
  },
  'profileMatch:unqualified': {
    key: 'profileMatchSatus',
    value: 'unqualified',
    label: 'Profile Match Unqualified',
    clearOthers: ['profileMatchSatus'],
  },
  'profileMatch:pending': {
    key: 'profileMatchSatus',
    value: 'NA',
    label: 'Profile Match Pending',
    clearOthers: ['profileMatchSatus'],
  },

  // AI Interviews Card
  'aiInterviews:qualified': {
    key: 'roundStatus',
    value: 'qualified',
    label: 'AI Interviews Qualified',
    clearOthers: ['roundStatus'],
  },
  'aiInterviews:partially_qualified': {
    key: 'roundStatus',
    value: 'partially_qualified',
    label: 'AI Interviews Partially Qualified',
    clearOthers: ['roundStatus'],
  },
  'aiInterviews:unqualified': {
    key: 'roundStatus',
    value: 'unqualified',
    label: 'AI Interviews Unqualified',
    clearOthers: ['roundStatus'],
  },
  'aiInterviews:pending': {
    key: 'roundStatus',
    value: 'pending',
    label: 'AI Interviews In Progress',
    clearOthers: ['roundStatus'],
  },

  // Online Interview Card
  'onlineInterview:scheduled': {
    key: 'interviewStatus',
    value: 'scheduled',
    label: 'Interview Scheduled',
    clearOthers: ['interviewStatus'],
  },
  'onlineInterview:completed': {
    key: 'interviewStatus',
    value: 'completed',
    label: 'Interview Completed',
    clearOthers: ['interviewStatus'],
  },
  'onlineInterview:not_scheduled': {
    key: 'interviewStatus',
    value: 'not_scheduled',
    label: 'Pending Slot Selection',
    clearOthers: ['interviewStatus'],
  },

  // Upload Type / Source Card
  'uploadType:company': {
    key: 'uploadType',
    value: 'company',
    label: 'File Uploads',
    clearOthers: ['uploadType'],
  },
  'uploadType:candidate': {
    key: 'uploadType',
    value: 'candidate',
    label: 'By Candidate',
    clearOthers: ['uploadType'],
  },
  'uploadType:lever': {
    key: 'uploadType',
    value: 'lever',
    label: 'ATS',
    clearOthers: ['uploadType'],
  },
  'uploadType:other': {
    key: 'uploadType',
    value: 'other',
    label: 'Other Sources',
    clearOthers: ['uploadType'],
  },
};

export interface UseApplicationFiltersReturn {
  filters: JobApplicationFilters;
  activeFilter: ActiveFilter | null;
  updateFilter: (key: keyof JobApplicationFilters, value: any, source?: 'card' | 'dropdown') => void;
  handleSegmentClick: (cardType: ActiveFilter['cardType'], status: string) => void;
  clearAllFilters: () => void;
  removeFilter: (key: keyof JobApplicationFilters) => void;
  getActiveFiltersList: () => Array<{ key: keyof JobApplicationFilters; value: string; label: string }>;
  syncDropdownToCard: (key: keyof JobApplicationFilters, value: any) => void;
  syncActiveFilterFromFilters: (filtersToSync: Partial<JobApplicationFilters>) => void;
}

const initialFilters: JobApplicationFilters = {
  status: null,
  interviewStatus: null,
  profileMatchSatus: null,
  roundStatus: null,
  search: null,
  sort: null,
  uploadType: null,
  requirementId: null,
  requirementMatch: null,
};

// Helper to derive activeFilter from filters state
const deriveActiveFilterFromFilters = (filters: Partial<JobApplicationFilters>): ActiveFilter | null => {
  // Check each filter and find the corresponding card type and status
  // Priority: roundStatus > profileMatchSatus > interviewStatus > uploadType > status
  
  if (filters.roundStatus) {
    const statusMap: Record<string, string> = {
      qualified: 'qualified',
      partially_qualified: 'partially_qualified',
      unqualified: 'unqualified',
      pending: 'pending',
    };
    if (statusMap[filters.roundStatus]) {
      return { cardType: 'aiInterviews', status: statusMap[filters.roundStatus] };
    }
  }

  if (filters.profileMatchSatus) {
    const statusMap: Record<string, string> = {
      qualified: 'qualified',
      unqualified: 'unqualified',
      NA: 'pending',
    };
    if (statusMap[filters.profileMatchSatus]) {
      return { cardType: 'profileMatch', status: statusMap[filters.profileMatchSatus] };
    }
  }

  if (filters.interviewStatus) {
    const statusMap: Record<string, string> = {
      scheduled: 'scheduled',
      completed: 'completed',
      not_scheduled: 'not_scheduled',
    };
    if (statusMap[filters.interviewStatus]) {
      return { cardType: 'onlineInterview', status: statusMap[filters.interviewStatus] };
    }
  }

  if (filters.uploadType) {
    const statusMap: Record<string, string> = {
      company: 'company',
      candidate: 'candidate',
      lever: 'lever',
      other: 'other',
    };
    if (statusMap[filters.uploadType]) {
      return { cardType: 'uploadType', status: statusMap[filters.uploadType] };
    }
  }

  if (filters.status) {
    const statusMap: Record<string, string> = {
      hired: 'hired',
      rejected: 'rejected',
      shortlisted: 'shortlisted',
      interview: 'interviewScheduled',
    };
    if (statusMap[filters.status]) {
      // Check if it's interview with scheduled status - use processStatus card
      if (filters.status === 'interview' && filters.interviewStatus === 'scheduled') {
        return { cardType: 'processStatus', status: 'interviewScheduled' };
      }
      return { cardType: 'applicationStatus', status: statusMap[filters.status] };
    }
  }

  return null;
};

export const useApplicationFilters = (
  initialFiltersOverride?: Partial<JobApplicationFilters>
): UseApplicationFiltersReturn => {
  const mergedInitialFilters = {
    ...initialFilters,
    ...initialFiltersOverride,
  };
  
  const [filters, setFilters] = useState<JobApplicationFilters>(mergedInitialFilters);
  
  // Derive initial activeFilter from the restored filters
  const [activeFilter, setActiveFilter] = useState<ActiveFilter | null>(() => 
    initialFiltersOverride ? deriveActiveFilterFromFilters(initialFiltersOverride) : null
  );

  // Get filter mapping for card segment
  const getFilterMapping = useCallback(
    (cardType: ActiveFilter['cardType'], status: string): FilterDefinition | null => {
      const mappingKey = `${cardType}:${status}`;
      return FILTER_MAPPINGS[mappingKey] || null;
    },
    []
  );

  // Get label for filter key-value pair
  const getFilterLabel = useCallback((key: keyof JobApplicationFilters, value: any): string => {
    // Find mapping that matches this key-value
    const mapping = Object.entries(FILTER_MAPPINGS).find(
      ([_, def]) => def.key === key && def.value === value
    );
    if (mapping) return mapping[1].label;

    // Fallback labels
    const labelMap: Record<string, Record<string, string>> = {
      status: {
        hired: 'Hired',
        rejected: 'Rejected',
        shortlisted: 'Shortlisted',
        interview: 'Interview',
      },
      profileMatchSatus: {
        qualified: 'Profile Match Qualified',
        unqualified: 'Profile Match Unqualified',
        NA: 'Profile Match Pending',
      },
      roundStatus: {
        qualified: 'AI Interviews Qualified',
        partially_qualified: 'AI Interviews Partially Qualified',
        unqualified: 'AI Interviews Unqualified',
        pending: 'AI Interviews In Progress',
      },
      interviewStatus: {
        scheduled: 'Interview Scheduled',
        completed: 'Interview Completed',
        not_scheduled: 'Pending Slot Selection',
      },
      sort: {
        latest_first: 'Latest First',
        oldest_first: 'Oldest First',
        profile_score_highest: 'Profile Score Highest',
        profile_score_lowest: 'Profile Score Lowest',
      },
      uploadType: {
        company: 'File Uploads',
        candidate: 'By Candidate',
        lever: 'ATS',
        other: 'Other Sources',
      },
      requirementMatch: {
        matched: 'Requirement Matched',
        unmatched: 'Requirement Unmatched',
        partial: 'Requirement Partial Match',
      },
    };

    // Handle requirementId specially
    if (key === 'requirementId') {
      return `Requirement #${value}`;
    }

    return labelMap[key]?.[value] || `${key}: ${value}`;
  }, []);

  // Update filter with sync logic
  const updateFilter = useCallback(
    (key: keyof JobApplicationFilters, value: any, source: 'card' | 'dropdown' = 'dropdown') => {
      setFilters((prev) => {
        const newFilters = { ...prev, [key]: value };

        // Special handling for interviewScheduled
        if (key === 'status' && value === 'interview') {
          // Check if this came from interviewScheduled card
          if (activeFilter?.cardType === 'processStatus' && activeFilter?.status === 'interviewScheduled') {
            newFilters.interviewStatus = 'scheduled';
          }
        } else if (key === 'status' && value !== 'interview') {
          // Clear interviewStatus if status is not interview
          newFilters.interviewStatus = null;
        }

        return newFilters;
      });
    },
    [activeFilter]
  );

  // Clear all filters - defined before handleSegmentClick which uses it
  const clearAllFilters = useCallback(() => {
    setFilters(initialFilters);
    setActiveFilter(null);
  }, []);

  // Handle card segment click
  const handleSegmentClick = useCallback(
    (cardType: ActiveFilter['cardType'], status: string) => {
      const mapping = getFilterMapping(cardType, status);

      if (!mapping) return;

      // Special case: clicking "total" should clear all filters and show all applications
      if (status === 'total' && mapping.value === null) {
        clearAllFilters();
        return;
      }

      // If clicking the same segment, deselect it (clear filter)
      if (
        activeFilter?.cardType === cardType &&
        activeFilter?.status === status &&
        filters[mapping.key] === mapping.value
      ) {
        setActiveFilter(null);
        updateFilter(mapping.key, null, 'card');
        return;
      }

      // Set active filter
      setActiveFilter({ cardType, status });

      // Clear conflicting filters
      if (mapping.clearOthers) {
        setFilters((prev) => {
          const newFilters = { ...prev };
          mapping.clearOthers?.forEach((key) => {
            if (key !== mapping.key) {
              newFilters[key] = null;
            }
          });
          return newFilters;
        });
      }

      // Apply the filter
      updateFilter(mapping.key, mapping.value, 'card');

      // Handle special case for interviewScheduled
      if (cardType === 'processStatus' && status === 'interviewScheduled') {
        updateFilter('interviewStatus', 'scheduled', 'card');
      }
    },
    [activeFilter, filters, getFilterMapping, updateFilter, clearAllFilters]
  );

  // Remove specific filter
  const removeFilter = useCallback((key: keyof JobApplicationFilters) => {
    setFilters((prev) => ({ ...prev, [key]: null }));

    // Clear activeFilter if it matches
    if (activeFilter) {
      const mapping = getFilterMapping(activeFilter.cardType, activeFilter.status);
      if (mapping?.key === key) {
        setActiveFilter(null);
      }
    }

    // Clear interviewStatus if status is cleared
    if (key === 'status') {
      setFilters((prev) => ({ ...prev, interviewStatus: null }));
    }
  }, [activeFilter, getFilterMapping]);

  // Get list of active filters for display
  const getActiveFiltersList = useCallback(() => {
    const activeFilters: Array<{ key: keyof JobApplicationFilters; value: string; label: string }> = [];

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        activeFilters.push({
          key: key as keyof JobApplicationFilters,
          value: String(value),
          label: getFilterLabel(key as keyof JobApplicationFilters, value),
        });
      }
    });

    return activeFilters;
  }, [filters, getFilterLabel]);

  // Sync dropdown to card selection (for when card is clicked)
  const syncDropdownToCard = useCallback((key: keyof JobApplicationFilters, value: any) => {
    // This is handled automatically by the filter state
    // Dropdowns read from filters state, so they'll update automatically
  }, []);

  // Sync activeFilter from filters (used when restoring from URL)
  const syncActiveFilterFromFilters = useCallback((filtersToSync: Partial<JobApplicationFilters>) => {
    const derivedActiveFilter = deriveActiveFilterFromFilters(filtersToSync);
    setActiveFilter(derivedActiveFilter);
  }, []);

  return {
    filters,
    activeFilter,
    updateFilter,
    handleSegmentClick,
    clearAllFilters,
    removeFilter,
    getActiveFiltersList,
    syncDropdownToCard,
    syncActiveFilterFromFilters,
  };
};















